/**
 * FutureBotProvider — page-awareness backbone for the FutureBot widget.
 *
 * Lives INSIDE the Router (needs useLocation) and above all routed pages so:
 *   1. It always knows the current route and resolves it to a pageId/title
 *      via the static pageRegistry.
 *   2. Any page can register live agent capabilities with useAgentPage():
 *      tool implementations the model can invoke (executed right here in the
 *      browser) plus a getContext() snapshot for the system prompt.
 *
 * A live page registration always wins over the static registry entry.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { matchCurrentPage } from './pageRegistry';

const FutureBotContext = createContext(null);

export function FutureBotProvider({ children }) {
  const location = useLocation();
  const [registration, setRegistration] = useState(null);

  /**
   * Register live agent capabilities for the current page.
   * Returns an unregister function (call it on unmount).
   *
   * registration = {
   *   pageId: string,
   *   title: string,
   *   tools: { [toolName]: async (args) => resultObject },
   *   getContext: async () => ({ referenceData, stateSummary }),
   *   suggestions?: [string],  // quick-start chips shown in an empty chat
   * }
   */
  const registerAgentPage = useCallback((reg) => {
    setRegistration(reg);
    return () => {
      setRegistration((current) => (current === reg ? null : current));
    };
  }, []);

  const currentPage = useMemo(() => {
    const staticPage = matchCurrentPage(location.pathname);
    return {
      route: location.pathname,
      pageId: registration?.pageId || staticPage.pageId,
      title: registration?.title || staticPage.title,
      tools: registration?.tools || null,
      getContext: registration?.getContext || null,
      suggestions: registration?.suggestions || null,
    };
  }, [location.pathname, registration]);

  const value = useMemo(
    () => ({ currentPage, registerAgentPage }),
    [currentPage, registerAgentPage]
  );

  return <FutureBotContext.Provider value={value}>{children}</FutureBotContext.Provider>;
}

export function useFutureBot() {
  const ctx = useContext(FutureBotContext);
  if (!ctx) {
    throw new Error('useFutureBot must be used within a FutureBotProvider');
  }
  return ctx;
}

/**
 * Page-side hook: register this page's agent capabilities while mounted.
 * Pass null to skip (e.g. read-only mode). The registration object should be
 * memoized by the caller; its tools should read fresh state via refs.
 */
export function useAgentPage(registration) {
  const { registerAgentPage } = useFutureBot();
  React.useEffect(() => {
    if (!registration) return undefined;
    return registerAgentPage(registration);
  }, [registerAgentPage, registration]);
}

export default FutureBotContext;
